import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Plus, Trash2, Copy, Globe, Languages, Info, Palette } from 'lucide-react';
import { auth } from '../../../firebase';
import QRCodeGenerator from '../ui/QRCodeGenerator';

const DEFAULT_THEME = {
  backgroundStart: '#0f172a',
  backgroundMid: '#111827',
  backgroundEnd: '#1f2937',
  accent: '#38bdf8',
  accentSoft: 'rgba(56, 189, 248, 0.16)',
  text: '#f8fafc',
  textMuted: '#cbd5e1'
};

const DEFAULT_GUIDE = {
  id: '',
  language: '',
  label: '',
  description: '',
  url: '',
  badge: '',
  imageUrl: '',
  featured: false,
  siblings: []
};

const DEFAULT_SIBLING_GUIDE = {
  id: '',
  language: '',
  label: '',
  description: '',
  url: '',
  badge: '',
  imageUrl: '',
  featured: false
};

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const DEFAULT_VENUE_PAGE = {
  entityType: 'venue',
  qrCodeId: '',
  displayName: '',
  welcomeTitle: 'Welcome',
  welcomeMessage: 'Choose a guide below to start your visit.',
  logoUrl: '',
  heroImageUrl: '',
  info: {
    label: 'Venue info',
    url: ''
  },
  theme: { ...DEFAULT_THEME },
  guides: []
};

const VENUE_STORAGE_ENDPOINT = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendDocumentUploaderToFirebase';
const PUBLIC_SITE_ORIGIN = 'https://guidesofia.com';

const ENTITY_OPTIONS = [
  { value: 'venue', label: 'Venue' },
  { value: 'museum', label: 'Museum' },
  { value: 'monument', label: 'Monument' },
  { value: 'business', label: 'Business' },
  { value: 'site', label: 'Site' },
  { value: 'place', label: 'Place' }
];

const stripJsonExtension = (value) => String(value || '').replace(/\.json$/i, '');

const isNumericQrId = (value) => /^\d{10,}$/.test(String(value || '').trim());

const generateVenueQrCodeId = () => String(Date.now());

const resolveVenueQrCodeId = (metadata = {}, venuePage = null, fallbackQrCodeId = generateVenueQrCodeId()) => {
  const source =
    venuePage?.qrCodeId ||
    metadata.venue_qr_code_id ||
    metadata.venue_page_id ||
    stripJsonExtension(metadata.filename) ||
    metadata.id;

  if (isNumericQrId(source)) {
    return String(source).trim();
  }

  return fallbackQrCodeId;
};

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const normalizeHexColor = (value, fallback = '#000000') => {
  const trimmed = String(value || '').trim();
  const normalized = trimmed.replace(/^#+/, '#');
  return HEX_COLOR_PATTERN.test(normalized) ? normalized : fallback;
};

const normalizeSiblingGuide = (guide = {}, index = 0) => ({
  ...deepClone(DEFAULT_SIBLING_GUIDE),
  ...guide,
  id: guide.id || `guide-sibling-${index + 1}`,
  language: guide.language || guide.languageCode || ''
});

const normalizeGuide = (guide = {}, index = 0) => ({
  ...deepClone(DEFAULT_GUIDE),
  ...guide,
  id: guide.id || `guide-${index + 1}`,
  language: guide.language || guide.languageCode || '',
  siblings: Array.isArray(guide.siblings)
    ? guide.siblings.map((sibling, siblingIndex) => normalizeSiblingGuide(sibling, siblingIndex))
    : []
});

const serializeGuide = (guide = {}) => {
  const serialized = {
    id: guide.id || '',
    language: guide.language || '',
    label: guide.label || '',
    url: guide.url || ''
  };

  if (guide.description) serialized.description = guide.description;
  if (guide.imageUrl) serialized.imageUrl = guide.imageUrl;
  if (guide.badge) serialized.badge = guide.badge;
  if (guide.featured) serialized.featured = true;
  if (Array.isArray(guide.siblings) && guide.siblings.length > 0) {
    serialized.siblings = guide.siblings.map((sibling) => serializeGuide({ ...sibling, siblings: [] }));
  }

  return serialized;
};

const normalizeVenuePage = (page = {}) => ({
  ...deepClone(DEFAULT_VENUE_PAGE),
  ...page,
  info: {
    ...DEFAULT_VENUE_PAGE.info,
    ...(page.info || {})
  },
  theme: {
    ...DEFAULT_THEME,
    ...(page.theme || {})
  },
  guides: Array.isArray(page.guides) ? page.guides.map((guide, index) => normalizeGuide(guide, index)) : []
});

const buildVenuePagePayload = (itinerary, venuePage, fallbackQrCodeId) => {
  const metadata = itinerary?.metadata || {};
  const normalized = normalizeVenuePage(venuePage);
  const businessInfo = metadata.museum_info || {};
  const qrCodeId = resolveVenueQrCodeId(metadata, normalized, fallbackQrCodeId);
  const publicQrUrl = `https://guidesofia.com/qr/${encodeURIComponent(qrCodeId)}`;
  const storagePath = `special_itineraries/venue-pages/${qrCodeId}.json`;

  return {
    qr_code: qrCodeId,
    qr_type: 'venue',
    qr_url: publicQrUrl,
    storage_path: storagePath,
    itinerary_id: metadata.id || null,
    museumId: qrCodeId,
    entityType: normalized.entityType || 'venue',
    qrCodeId,
    displayName: normalized.displayName || metadata.name || 'Venue',
    museumName: normalized.displayName || metadata.name || 'Venue',
    welcomeTitle: normalized.welcomeTitle || 'Welcome',
    welcomeMessage: normalized.welcomeMessage || 'Choose a guide below to start your visit.',
    logoUrl: normalized.logoUrl || '',
    heroImageUrl: normalized.heroImageUrl || '',
    info: {
      label: normalized.info?.label || 'Venue info',
      url: normalized.info?.url || ''
    },
    theme: normalized.theme,
    museumInfo: {
      description: businessInfo.description || metadata.description || '',
      opening_hours: businessInfo.opening_hours || normalized.info?.opening_hours || '',
      admission: businessInfo.admission || normalized.info?.admission || '',
      address: businessInfo.address || normalized.info?.address || metadata.address || '',
      accessibility: businessInfo.accessibility || normalized.info?.accessibility || '',
      wifi: businessInfo.wifi || normalized.info?.wifi || '',
      gift_shop: businessInfo.gift_shop || normalized.info?.gift_shop || '',
      cafe: businessInfo.cafe || normalized.info?.cafe || '',
      phone: businessInfo.phone || normalized.info?.phone || metadata.phone || '',
      email: businessInfo.email || normalized.info?.email || metadata.email || '',
      website: businessInfo.website || normalized.info?.website || metadata.website || '',
      languages: normalized.info?.languages || ''
    },
    guides: normalized.guides.map((guide) => serializeGuide(guide))
  };
};

const buildVenueQrSql = (payload) => {
  const qrCode = String(payload?.qr_code || '').trim();
  const qrUrl = String(payload?.qr_url || '').trim();
  const storagePath = String(payload?.storage_path || '').trim();
  const itineraryIdRaw = String(payload?.itinerary_id || '').trim();
  const guideName = String(payload?.displayName || payload?.museumName || 'Venue').replace(/'/g, "''");
  const description = String(payload?.welcomeMessage || 'Venue landing page.').replace(/'/g, "''");
  const itineraryIdSql = /^\d+$/.test(itineraryIdRaw) ? `'${itineraryIdRaw}'` : 'NULL';

  if (!qrCode || !qrUrl || !storagePath) {
    return '';
  }

  return `INSERT INTO \`qr_codes\` (
  \`qr_code\`,
  \`qr_type\`,
  \`full_qr_code\`,
  \`guide_path\`,
  \`stop_id\`,
  \`tier\`,
  \`created_by\`,
  \`is_active\`,
  \`guide_name\`,
  \`stop_name\`,
  \`description\`,
  \`location_lat\`,
  \`location_lng\`,
  \`Itinerary_ID\`
) VALUES (
  '${qrCode}',
  'venue',
  '${qrUrl}',
  '${storagePath}',
  NULL,
  'free',
  'admin',
  1,
  '${guideName}',
  NULL,
  '${description}',
  NULL,
  NULL,
  ${itineraryIdSql}
)
ON DUPLICATE KEY UPDATE
  \`qr_type\` = VALUES(\`qr_type\`),
  \`full_qr_code\` = VALUES(\`full_qr_code\`),
  \`guide_path\` = VALUES(\`guide_path\`),
  \`stop_id\` = VALUES(\`stop_id\`),
  \`tier\` = VALUES(\`tier\`),
  \`guide_name\` = VALUES(\`guide_name\`),
  \`stop_name\` = VALUES(\`stop_name\`),
  \`description\` = VALUES(\`description\`),
  \`location_lat\` = VALUES(\`location_lat\`),
  \`location_lng\` = VALUES(\`location_lng\`),
  \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);`;
};

const VenueLandingPageSection = ({ itinerary, setItinerary }) => {
  const initialVenuePage = useMemo(() => normalizeVenuePage(itinerary?.metadata?.venue_page), [itinerary?.metadata?.venue_page]);
  const [copied, setCopied] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [publishState, setPublishState] = useState({ loading: false, url: '', error: '' });
  const venueQrCodeFallbackRef = useRef(generateVenueQrCodeId());

  useEffect(() => {
    if (!itinerary.metadata.venue_page) {
      const existingQrCodeId = itinerary.metadata?.venue_page_id;
      setItinerary((prev) => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          venue_page: normalizeVenuePage({
            qrCodeId: isNumericQrId(existingQrCodeId) ? String(existingQrCodeId) : venueQrCodeFallbackRef.current,
            displayName: prev.metadata?.name || '',
            welcomeTitle: prev.metadata?.name ? `Welcome to ${prev.metadata.name}` : 'Welcome'
          })
        }
      }));
    }
  }, [itinerary.metadata.venue_page, setItinerary]);

  const venuePage = useMemo(
    () => normalizeVenuePage(itinerary?.metadata?.venue_page || initialVenuePage),
    [itinerary?.metadata?.venue_page, initialVenuePage]
  );

  const pageId = resolveVenueQrCodeId(itinerary?.metadata, venuePage, venueQrCodeFallbackRef.current);
  const publicBasePath = `/qr/${encodeURIComponent(pageId)}`;
  const publicQrUrl = `${PUBLIC_SITE_ORIGIN}${publicBasePath}`;
  const infoPath = `${publicBasePath}/info`;
  const publicFolder = venuePage.entityType === 'museum' ? 'museum-pages' : 'venue-pages';
  const storagePath = `special_itineraries/venue-pages/${pageId}.json`;
  const exportFileName = `${pageId}.json`;

  const updateVenuePage = (updater) => {
    setItinerary((prev) => {
      const current = normalizeVenuePage(prev.metadata?.venue_page);
      const next = typeof updater === 'function' ? updater(current) : updater;
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          venue_page: normalizeVenuePage(next)
        }
      };
    });
  };

  const updateField = (field, value) => {
    if (field === 'qrCodeId') {
      const digitsOnly = String(value || '').replace(/\D+/g, '');
      const nextId = digitsOnly.length >= 10 ? digitsOnly : venueQrCodeFallbackRef.current;
      updateVenuePage((prev) => ({ ...prev, qrCodeId: nextId }));
      return;
    }
    updateVenuePage((prev) => ({ ...prev, [field]: value }));
  };

  const updateInfoField = (field, value) => {
    updateVenuePage((prev) => ({
      ...prev,
      info: {
        ...prev.info,
        [field]: value
      }
    }));
  };

  const updateThemeField = (field, value) => {
    updateVenuePage((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        [field]: value
      }
    }));
  };

  const updateHexThemeField = (field, value) => {
    updateThemeField(field, normalizeHexColor(value, normalizeHexColor(venuePage.theme[field], '#000000')));
  };

  const updateGuide = (index, field, value) => {
    updateVenuePage((prev) => ({
      ...prev,
      guides: prev.guides.map((guide, guideIndex) => (
        guideIndex === index ? { ...guide, [field]: value } : guide
      ))
    }));
  };

  const addGuide = () => {
    updateVenuePage((prev) => ({
      ...prev,
      guides: [...prev.guides, { ...deepClone(DEFAULT_GUIDE), id: `guide-${prev.guides.length + 1}` }]
    }));
  };

  const updateGuideSibling = (guideIndex, siblingIndex, field, value) => {
    updateVenuePage((prev) => ({
      ...prev,
      guides: prev.guides.map((guide, currentGuideIndex) => {
        if (currentGuideIndex !== guideIndex) return guide;
        return {
          ...guide,
          siblings: guide.siblings.map((sibling, currentSiblingIndex) => (
            currentSiblingIndex === siblingIndex ? { ...sibling, [field]: value } : sibling
          ))
        };
      })
    }));
  };

  const addGuideSibling = (guideIndex) => {
    updateVenuePage((prev) => ({
      ...prev,
      guides: prev.guides.map((guide, currentGuideIndex) => {
        if (currentGuideIndex !== guideIndex) return guide;
        return {
          ...guide,
          siblings: [
            ...guide.siblings,
            {
              ...deepClone(DEFAULT_SIBLING_GUIDE),
              id: `${guide.id || `guide-${guideIndex + 1}`}-sibling-${guide.siblings.length + 1}`
            }
          ]
        };
      })
    }));
  };

  const removeGuideSibling = (guideIndex, siblingIndex) => {
    updateVenuePage((prev) => ({
      ...prev,
      guides: prev.guides.map((guide, currentGuideIndex) => {
        if (currentGuideIndex !== guideIndex) return guide;
        return {
          ...guide,
          siblings: guide.siblings.filter((_, currentSiblingIndex) => currentSiblingIndex !== siblingIndex)
        };
      })
    }));
  };

  const removeGuide = (index) => {
    updateVenuePage((prev) => ({
      ...prev,
      guides: prev.guides.filter((_, guideIndex) => guideIndex !== index)
    }));
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const downloadJson = () => {
    const payload = buildVenuePagePayload(itinerary, venuePage, venueQrCodeFallbackRef.current);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFileName;
    a.click();
    URL.revokeObjectURL(url);
    setDownloadMessage(`Downloaded ${exportFileName}`);
    setTimeout(() => setDownloadMessage(''), 2500);
  };

  const publishPayloadToFirebase = useCallback(async (payload) => {
    try {
      setPublishState({ loading: true, url: '', error: '' });

      const filename = `${pageId}.json`;
      const storagePath = `special_itineraries/venue-pages/${filename}`;
      const token = await auth.currentUser?.getIdToken(true);

      if (!token) {
        throw new Error('You must be signed in to publish venue pages.');
      }

      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const formData = new FormData();
      formData.append('document', blob, filename);
      formData.append('storagePath', storagePath);
      formData.append('country', 'venue-pages');
      formData.append('itineraryName', pageId);

      const response = await fetch(VENUE_STORAGE_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to publish venue page');
      }

      setPublishState({
        loading: false,
        url: result.url || `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/${storagePath}`,
        error: ''
      });
    } catch (error) {
      console.error('Failed to publish venue page:', error);
      setPublishState({
        loading: false,
        url: '',
        error: error.message || 'Failed to publish venue page'
      });
    }
  }, [pageId]);

  const publishToFirebase = async () => {
    await publishPayloadToFirebase(buildVenuePagePayload(itinerary, venuePage, venueQrCodeFallbackRef.current));
  };

  const copyVenueSql = async () => {
    const payload = buildVenuePagePayload(itinerary, venuePage, venueQrCodeFallbackRef.current);
    const sql = buildVenueQrSql(payload);
    if (!sql) {
      setDownloadMessage('Could not build SQL yet');
      setTimeout(() => setDownloadMessage(''), 2500);
      return;
    }
    await copyText(sql);
    setDownloadMessage('Copied QR SQL');
    setTimeout(() => setDownloadMessage(''), 2500);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-sky-900">Venue Landing Page</h2>
            <p className="text-sm text-sky-700">
              Configure the public QR landing page for this guide. The JSON export can be placed in <code className="rounded bg-sky-100 px-1">{publicFolder}</code>.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => copyText(publicQrUrl)}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
            >
              <Copy className="h-4 w-4" />
              Copy QR URL
            </button>
            <button
              onClick={downloadJson}
              className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
            >
              <Download className="h-4 w-4" />
              Download JSON
            </button>
            <button
              onClick={copyVenueSql}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
            >
              <Copy className="h-4 w-4" />
              Copy QR SQL
            </button>
            <QRCodeGenerator qrValue={publicQrUrl} stopName={`${pageId}-venue`}>
              QR Maker
            </QRCodeGenerator>
            <button
              onClick={publishToFirebase}
              disabled={publishState.loading}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
            >
              {publishState.loading ? 'Publishing...' : 'Publish to Firebase'}
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs text-sky-700">
          <span className="rounded-full bg-sky-100 px-2 py-1">QR ID: {pageId}</span>
          <span className="rounded-full bg-sky-100 px-2 py-1">QR route: {publicBasePath}</span>
          <span className="rounded-full bg-sky-100 px-2 py-1">Info route: {infoPath}</span>
          <span className="rounded-full bg-sky-100 px-2 py-1">Storage path: {storagePath}</span>
          <span className="rounded-full bg-sky-100 px-2 py-1">Export file: {exportFileName}</span>
          {copied && <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700">Copied</span>}
          {downloadMessage && <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700">{downloadMessage}</span>}
          {publishState.url && (
            <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700">
              Published to Firebase
            </span>
          )}
          {publishState.error && (
            <span className="rounded-full bg-red-100 px-2 py-1 text-red-700">
              {publishState.error}
            </span>
          )}
        </div>
        {publishState.url && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            <div className="break-all">{publishState.url}</div>
            <button
              onClick={() => copyText(publishState.url)}
              className="mt-2 inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <Copy className="h-4 w-4" />
              Copy public URL
            </button>
          </div>
        )}
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
          <p className="font-medium">Where the JSON is saved</p>
          <p className="mt-1 break-all font-mono text-xs text-gray-600">{storagePath}</p>
          <p className="mt-2 text-xs text-gray-500">
            In Firebase Console, check Storage, not Firestore.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Info className="h-5 w-5 text-sky-600" />
              Basic details
            </h3>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Entity type</label>
                <select
                  value={venuePage.entityType}
                  onChange={(e) => updateField('entityType', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                >
                  {ENTITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">QR ID</label>
                <input
                  type="text"
                  value={venuePage.qrCodeId || pageId}
                  onChange={(e) => updateField('qrCodeId', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2 font-mono"
                  placeholder="Auto-generated numeric QR ID"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This becomes the public QR path: <code>/qr/{pageId || '...'}</code>
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Display name</label>
                <input
                  type="text"
                  value={venuePage.displayName}
                  onChange={(e) => updateField('displayName', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="Museum / Monument / Business name"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Welcome title</label>
                <input
                  type="text"
                  value={venuePage.welcomeTitle}
                  onChange={(e) => updateField('welcomeTitle', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="Welcome"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Welcome message</label>
                <textarea
                  value={venuePage.welcomeMessage}
                  onChange={(e) => updateField('welcomeMessage', e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="Choose a guide below to start your visit."
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Globe className="h-5 w-5 text-sky-600" />
              Branding and info
            </h3>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Logo URL</label>
                <input
                  type="text"
                  value={venuePage.logoUrl}
                  onChange={(e) => updateField('logoUrl', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Hero image URL</label>
                <input
                  type="text"
                  value={venuePage.heroImageUrl}
                  onChange={(e) => updateField('heroImageUrl', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Info button label</label>
                <input
                  type="text"
                  value={venuePage.info.label}
                  onChange={(e) => updateInfoField('label', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="Venue info"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Info page URL</label>
                <input
                  type="text"
                  value={venuePage.info.url}
                  onChange={(e) => updateInfoField('url', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="/venue/your-slug/info"
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Palette className="h-5 w-5 text-sky-600" />
              Theme
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                ['backgroundStart', 'Background start'],
                ['backgroundMid', 'Background mid'],
                ['backgroundEnd', 'Background end'],
                ['accent', 'Accent color'],
                ['text', 'Text color'],
                ['textMuted', 'Text muted']
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={normalizeHexColor(venuePage.theme[key], key === 'textMuted' ? '#cbd5e1' : '#0f172a')}
                      onChange={(e) => updateHexThemeField(key, e.target.value)}
                      className="h-11 w-16 cursor-pointer rounded-lg border border-gray-300 bg-white p-1"
                      aria-label={`${label} color picker`}
                    />
                    <input
                      type="text"
                      value={venuePage.theme[key]}
                      onChange={(e) => updateThemeField(key, e.target.value)}
                      className="w-full rounded-lg border border-gray-300 p-2"
                      placeholder={key === 'textMuted' ? '#cbd5e1' : '#0f172a'}
                    />
                  </div>
                </div>
              ))}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Accent soft</label>
                <input
                  type="text"
                  value={venuePage.theme.accentSoft}
                  onChange={(e) => updateThemeField('accentSoft', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2"
                  placeholder="rgba(56, 189, 248, 0.16)"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Languages className="h-5 w-5 text-sky-600" />
                Guide cards
              </h3>
              <button
                onClick={addGuide}
                className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                <Plus className="h-4 w-4" />
                Add guide
              </button>
            </div>

            <div className="mb-4 rounded-lg border border-sky-100 bg-sky-50 p-3 text-xs text-sky-900">
              Each top-level guide must include <code>id</code>, <code>language</code>, <code>label</code>, and <code>url</code>.
              Use <code>siblings</code> only for alternate-language versions of the same guide. Do not use <code>languageCode</code>.
            </div>

            <div className="space-y-4">
              {venuePage.guides.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                  No guide cards yet. Add one top-level guide per guide family, then attach alternate languages as siblings.
                </div>
              ) : venuePage.guides.map((guide, index) => (
                <div key={guide.id || index} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-700">Guide {index + 1}</div>
                    <button
                      onClick={() => removeGuide(index)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">ID</label>
                      <input
                        type="text"
                        value={guide.id}
                        onChange={(e) => updateGuide(index, 'id', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="adult-guide"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Language</label>
                      <input
                        type="text"
                        value={guide.language}
                        onChange={(e) => updateGuide(index, 'language', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="en"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Badge</label>
                      <input
                        type="text"
                        value={guide.badge}
                        onChange={(e) => updateGuide(index, 'badge', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="Kids, EN, Family"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Label</label>
                      <input
                        type="text"
                        value={guide.label}
                        onChange={(e) => updateGuide(index, 'label', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="Adult Guide"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">URL</label>
                      <input
                        type="text"
                        value={guide.url}
                        onChange={(e) => updateGuide(index, 'url', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="/guides/your-guide"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-600">Description</label>
                      <textarea
                        rows={3}
                        value={guide.description}
                        onChange={(e) => updateGuide(index, 'description', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="Short description of what this guide is for."
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Image URL</label>
                      <input
                        type="text"
                        value={guide.imageUrl}
                        onChange={(e) => updateGuide(index, 'imageUrl', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={Boolean(guide.featured)}
                          onChange={(e) => updateGuide(index, 'featured', e.target.checked)}
                        />
                        Featured guide
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-700">Sibling guides</div>
                        <p className="text-xs text-gray-500">Alternate-language variants of this same guide only.</p>
                      </div>
                      <button
                        onClick={() => addGuideSibling(index)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                      >
                        <Plus className="h-4 w-4" />
                        Add sibling
                      </button>
                    </div>

                    <div className="space-y-3">
                      {guide.siblings.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-gray-200 p-3 text-xs text-gray-500">
                          No siblings yet. Add a sibling when this guide has another language version.
                        </div>
                      ) : guide.siblings.map((sibling, siblingIndex) => (
                        <div key={sibling.id || siblingIndex} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="text-xs font-semibold text-gray-700">
                              Sibling {siblingIndex + 1}
                            </div>
                            <button
                              onClick={() => removeGuideSibling(index, siblingIndex)}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          </div>

                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">ID</label>
                              <input
                                type="text"
                                value={sibling.id}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'id', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="adult-guide-pt"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">Language</label>
                              <input
                                type="text"
                                value={sibling.language}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'language', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="pt"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">Label</label>
                              <input
                                type="text"
                                value={sibling.label}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'label', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="Guia principal"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">URL</label>
                              <input
                                type="text"
                                value={sibling.url}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'url', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="/guides/your-guide-pt"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="mb-1 block text-xs font-medium text-gray-600">Description</label>
                              <textarea
                                rows={2}
                                value={sibling.description}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'description', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="Optional short description."
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">Image URL</label>
                              <input
                                type="text"
                                value={sibling.imageUrl}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'imageUrl', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="https://..."
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">Badge</label>
                              <input
                                type="text"
                                value={sibling.badge}
                                onChange={(e) => updateGuideSibling(index, siblingIndex, 'badge', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                                placeholder="PT"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={Boolean(sibling.featured)}
                                  onChange={(e) => updateGuideSibling(index, siblingIndex, 'featured', e.target.checked)}
                                />
                                Featured sibling
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default VenueLandingPageSection;
